import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react';
import {
  MagnifyingGlassIcon, WifiIcon, ArrowPathIcon, SignalIcon, CheckIcon,
} from '@heroicons/react/24/outline';
import { useApi, type DiscoveredCamera } from '../api';
import { usePermissions } from '../auth';
import BulkAddCredentialsModal, { type BulkAddCredentials } from '../components/cameras/BulkAddCredentialsModal';
import { PageTitle } from '../components/layout/TitleBar';
import {
  FilterColumn, FilterSection, FilterCheck, FilterSearch, PageWithFilters, useFilterColumn, FILTER_CONTROL,
} from '../components/layout/FilterColumn';
import FillHeightCard, { TABLE_HEAD, TABLE_TH, tableHeadRow } from '../components/layout/FillHeightCard';
import ListPager from '../components/common/ListPager';
import { BTN_PRIMARY, BTN_PRIMARY_SM, BTN_SECONDARY_SM } from '../components/common/ui';

export interface DiscoveryPageProps {
  /** Per-row / bulk add modal (node-specific CameraFormModal). */
  renderAddModal?: (camera: DiscoveredCamera, helpers: { onClose: () => void; onAdded: (ip: string) => void }) => ReactNode;
  /** When set, gates scan/rescan on this permission. Omit = always allowed. */
  discoverPermission?: string;
  /** When set, gates approving/adding a discovered camera on this permission. Omit = falls back to discoverPermission. */
  approvePermission?: string;
  /** Cloud: require a selected node (empty state when none). */
  requireNode?: boolean;
  /** Cloud: selected node id — changing it resets/refetches. */
  nodeKey?: string | null;
  /** Extra classes on the page body (the PageWithFilters). */
  className?: string;
}

const COMMON_SUBNETS = [
  '192.168.1.0/24', '192.168.2.0/24', '192.168.0.0/24', '10.0.0.0/24', '172.16.0.0/24',
];
const PAGE_SIZE = 50;

/**
 * ONVIF camera discovery — shared by both dashboards. The scan mechanism (socket stream
 * vs polling) lives behind useApi().discovery.watchScan; node-scoping is in the adapter;
 * the add-camera modal is a node-specific slot.
 */
export default function DiscoveryPage({ renderAddModal, discoverPermission, approvePermission, requireNode = false, nodeKey, className = '' }: DiscoveryPageProps) {
  const { discovery } = useApi();
  const { hasPermission } = usePermissions();
  const canDiscover = discoverPermission ? hasPermission(discoverPermission) : true;
  // Approving a discovered camera is a separate capability (local: discovery.approve);
  // when unspecified it follows canDiscover (cloud uses one discovery.view perm).
  const canApprove = approvePermission ? hasPermission(approvePermission) : canDiscover;

  const [subnet, setSubnet] = useState('');
  const [customSubnet, setCustomSubnet] = useState('');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState<DiscoveredCamera[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedIps, setSelectedIps] = useState<Set<string>>(new Set());
  const [addedIps, setAddedIps] = useState<Set<string>>(new Set());
  const [bulkAdding, setBulkAdding] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editCamera, setEditCamera] = useState<DiscoveredCamera | null>(null);

  const scanColumn = useFilterColumn('camera-discovery');

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopScanRef = useRef<(() => void) | null>(null);

  const activeSubnet = subnet === 'custom' ? customSubnet : subnet;
  const noNode = requireNode && !nodeKey;
  const isCustomSelected = subnet === 'custom' && !COMMON_SUBNETS.includes(customSubnet);

  // stop any active scan watcher on unmount / node change
  useEffect(() => () => { stopScanRef.current?.(); }, []);
  useEffect(() => {
    stopScanRef.current?.();
    stopScanRef.current = null;
    setScanning(false);
    setCameras([]);
    setTotalCount(0);
    // The "already added" marks are this node's registered IPs; another node's would label
    // rows as registered that this node has never seen. Re-read below.
    setAddedIps(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey]);

  // mark already-registered cameras
  useEffect(() => {
    if (noNode) return;
    discovery.listExistingCameraIps().then((ips) => { if (ips.length) setAddedIps(new Set(ips)); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey]);

  const fetchResults = useCallback(async (p: number, s: string) => {
    try {
      const res = await discovery.getResults({ page: p, limit: PAGE_SIZE, search: s });
      setCameras(res.data || []);
      setTotalCount(res.total || 0);
    } catch { /* ignore */ }
  }, [discovery]);

  function handleSearchChange(val: string) {
    setSearchInput(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setSearch(val); setPage(1); }, 300);
  }

  useEffect(() => {
    if (!scanning && totalCount > 0) fetchResults(page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  function runScan() {
    if (!activeSubnet) { setError('Please select or enter a subnet to scan.'); return; }
    setError('');
    setScanning(true);
    setCameras([]);
    setTotalCount(0);
    setPage(1);
    setSearch('');
    setSearchInput('');
    setSelectedIps(new Set());
    stopScanRef.current?.();
    discovery.startScan(activeSubnet)
      .then(() => {
        stopScanRef.current = discovery.watchScan({
          onResults: (cams, total) => { setCameras(cams); setTotalCount(total); },
          onDone: (err) => { setScanning(false); stopScanRef.current = null; if (err) setError(err); },
        });
      })
      .catch((err: any) => { setError(err?.message || 'Discovery failed'); setScanning(false); });
  }

  const toggleSelect = (ip: string) => setSelectedIps((prev) => {
    const next = new Set(prev);
    next.has(ip) ? next.delete(ip) : next.add(ip);
    return next;
  });

  const allPageSelected = useMemo(() => {
    const selectable = cameras.filter((c) => !addedIps.has(c.ip_address));
    return selectable.length > 0 && selectable.every((c) => selectedIps.has(c.ip_address));
  }, [cameras, selectedIps, addedIps]);

  const toggleSelectAll = () => {
    const selectable = cameras.filter((c) => !addedIps.has(c.ip_address));
    setSelectedIps((prev) => {
      const next = new Set(prev);
      if (allPageSelected) selectable.forEach((c) => next.delete(c.ip_address));
      else selectable.forEach((c) => next.add(c.ip_address));
      return next;
    });
  };

  // Cameras chosen for bulk add (selected on the current page, not already added).
  const selectedCameras = useMemo(
    () => cameras.filter((c) => selectedIps.has(c.ip_address) && !addedIps.has(c.ip_address)),
    [cameras, selectedIps, addedIps],
  );

  async function handleBulkAdd(creds: BulkAddCredentials) {
    if (selectedCameras.length === 0) return;
    setBulkAdding(true);
    setError('');
    try {
      // Same RTSP template + credentials applied to every selected camera; the
      // backend swaps the <IP> token for each camera's real IP on store.
      const toAdd = selectedCameras.map((c) => ({
        ip_address: c.ip_address,
        device_name: `camera-${c.ip_address}`,
        manufacturer: c.manufacturer,
        model: c.model,
        firmware: c.firmware,
        rtsp_urls: c.rtsp_urls || [],
        rtsp_url: creds.rtsp_url,
        sub_stream_rtsp_url: creds.sub_stream_rtsp_url || undefined,
        rtsp_username: creds.rtsp_username || undefined,
        rtsp_password: creds.rtsp_password || undefined,
      }));
      const result = await discovery.bulkApprove(toAdd);
      const newAdded = new Set(addedIps);
      toAdd.forEach((c) => newAdded.add(c.ip_address));
      if (result?.errors?.length) {
        result.errors.forEach((e: any) => newAdded.delete(e.ip));
        setError(`${result.errors.length} camera(s) failed to add`);
      }
      setAddedIps(newAdded);
      setSelectedIps(new Set());
      setShowBulkModal(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Bulk add failed');
    } finally {
      setBulkAdding(false);
    }
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (noNode) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <SignalIcon className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-[15px] font-medium">No site selected</p>
        <p className="mt-1 text-[13px]">Select a site from the title bar to run discovery.</p>
      </div>
    );
  }

  return (
    <>
      <PageTitle title="Camera Discovery" subtitle="Scan the network for ONVIF-compatible cameras and add them to the system" />

      {/* What to scan (and the results search) on the left, flush as on Search & Track. It must
          stay the page body — a direct child of the shell's <main> — so no wrapper around it. */}
      <PageWithFilters
        className={className}
        filters={(
          <FilterColumn
            width="wide"
            open={scanColumn.open}
            onToggle={scanColumn.toggle}
            label="Filters"
            activeCount={searchInput ? 1 : 0}
            onClearAll={() => handleSearchChange('')}
          >
            {/* Narrows the discovered list; a new scan clears it. */}
            <FilterSearch value={searchInput} onChange={handleSearchChange} placeholder="Search IP, name, manufacturer…" />
            <FilterSection title="Subnet">
              <div data-tour="discovery-subnets" className="space-y-0.5">
                {COMMON_SUBNETS.map((s) => (
                  <FilterCheck
                    key={s}
                    radio
                    checked={customSubnet === s && subnet === 'custom'}
                    onClick={() => { setSubnet('custom'); setCustomSubnet(s); setError(''); }}
                    label={<span className="font-mono">{s}</span>}
                  />
                ))}
                <FilterCheck
                  radio
                  checked={isCustomSelected}
                  onClick={() => { setSubnet('custom'); setCustomSubnet(''); setError(''); }}
                  label="Custom…"
                />
              </div>
              {isCustomSelected && (
                <input type="text" value={customSubnet} onChange={(e) => setCustomSubnet(e.target.value)}
                  placeholder="e.g. 192.168.10.0/24"
                  className={`${FILTER_CONTROL} font-mono`} />
              )}
            </FilterSection>
            <div className="space-y-1.5">
              {canDiscover && (
                <button data-tour="discovery-scan" onClick={runScan} disabled={scanning || !activeSubnet}
                  className={`${BTN_PRIMARY} w-full`}>
                  {scanning ? (<><ArrowPathIcon className="h-4 w-4 animate-spin" />Scanning…</>) : (<><MagnifyingGlassIcon className="h-4 w-4" />Scan Network</>)}
                </button>
              )}
              {activeSubnet && !scanning && (
                <p className="text-[11px] text-muted-foreground">Target: <span className="font-mono text-foreground">{activeSubnet}</span></p>
              )}
            </div>
          </FilterColumn>
        )}
      >
        {scanning && (
          <div className="flex items-center gap-3 rounded-xl border border-brand/30 bg-brand-light px-5 py-4">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand border-t-transparent" />
            <div className="flex-1">
              <p className="text-[13px] font-medium text-foreground">Scanning {activeSubnet}…</p>
              <p className="text-[12px] text-muted-foreground">
                {totalCount > 0 ? `${totalCount} camera(s) found so far` : 'Probing network for ONVIF cameras…'}
              </p>
            </div>
            {totalCount > 0 && (
              <span className="rounded-full bg-brand px-3 py-1 text-[14px] font-bold text-white">{totalCount}</span>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {error}
            <button onClick={() => setError('')} className="ml-2 underline">dismiss</button>
          </div>
        )}

        {(cameras.length > 0 || (!scanning && totalCount > 0)) && (
          // ONE white card filling the rest of the screen: its header strip (count, bulk add,
          // rescan) on top, then the table scrolling inside with a sticky — muted, since a strip
          // sits above it — column-title row, and the pager as its footer.
          <div data-tour="discovery-results">
            <FillHeightCard>
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <h3 className="text-[14px] font-semibold text-foreground">
                    Discovered <span className="text-brand">{totalCount}</span> camera{totalCount !== 1 ? 's' : ''}
                  </h3>
                  {canApprove && selectedIps.size > 0 && (
                    <button onClick={() => setShowBulkModal(true)} disabled={bulkAdding}
                      className={BTN_PRIMARY_SM}>
                      {bulkAdding ? (<><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />Adding…</>) : (<><CheckIcon className="h-3.5 w-3.5" />Add Selected ({selectedIps.size})</>)}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {canDiscover && !scanning && (
                    <button onClick={runScan} className={BTN_SECONDARY_SM}>
                      <ArrowPathIcon className="h-3.5 w-3.5" />Rescan
                    </button>
                  )}
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto">
                <table className="w-full text-left">
                  <thead className={TABLE_HEAD}>
                    <tr className={tableHeadRow(true)}>
                      <th className={`${TABLE_TH} w-10 pl-4`}>
                        <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll}
                          aria-label="Select all on this page"
                          className="h-3.5 w-3.5 rounded border-border cursor-pointer" />
                      </th>
                      <th className={TABLE_TH}>IP Address</th>
                      <th className={TABLE_TH}>Name</th>
                      <th className={TABLE_TH}>Manufacturer</th>
                      <th className={TABLE_TH}>Model</th>
                      <th className={TABLE_TH}>RTSP</th>
                      <th className={`${TABLE_TH} w-24 pr-4 !text-center`}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {cameras.map((cam) => {
                      const ip = cam.ip_address;
                      const isAdded = addedIps.has(ip);
                      const isSelected = selectedIps.has(ip);
                      const rtsp = Array.isArray(cam.rtsp_urls) && cam.rtsp_urls.length > 0 ? cam.rtsp_urls[0] : '';
                      return (
                        <tr key={ip} className={`text-[12px] transition-colors ${isAdded ? 'bg-green-50/50 dark:bg-green-900/10' : isSelected ? 'bg-brand-light' : 'hover:bg-muted'}`}>
                          <td className="py-2.5 pl-4 pr-3">
                            {isAdded ? (
                              <CheckIcon className="h-4 w-4 text-green-600" />
                            ) : (
                              <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(ip)}
                                className="h-3.5 w-3.5 rounded border-border cursor-pointer" />
                            )}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-foreground">{ip}</td>
                          <td className="px-3 py-2.5 text-foreground max-w-[180px] truncate">{cam.device_name || cam.name || '—'}</td>
                          <td className="px-3 py-2.5 text-muted-foreground">{cam.manufacturer || '—'}</td>
                          <td className="px-3 py-2.5 text-muted-foreground">{cam.model || '—'}</td>
                          <td className="px-3 py-2.5 font-mono text-muted-foreground max-w-[200px] truncate text-[11px]">{rtsp || '—'}</td>
                          <td className="py-2.5 pl-3 pr-4 text-center">
                            {isAdded ? (
                              <span className="text-[11px] font-semibold text-green-600">Added ✓</span>
                            ) : canApprove && renderAddModal ? (
                              <button onClick={() => setEditCamera(cam)}
                                className="rounded-lg bg-brand px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-dark transition-colors">Add</button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="shrink-0 border-t border-border px-4 py-2">
                  <ListPager bare page={page} pageSize={PAGE_SIZE} total={totalCount} onPageChange={setPage} itemLabel="cameras" />
                </div>
              )}
            </FillHeightCard>
          </div>
        )}

        {!scanning && cameras.length === 0 && totalCount === 0 && (
          <div data-tour="discovery-results" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <WifiIcon className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-[15px] font-medium">No cameras discovered</p>
            <p className="mt-1 text-[13px]">Select a subnet and click "Scan Network" to discover cameras.</p>
          </div>
        )}
      </PageWithFilters>

      {editCamera && renderAddModal?.(editCamera, {
        onClose: () => setEditCamera(null),
        onAdded: (ip) => { setAddedIps((prev) => new Set(prev).add(ip)); setEditCamera(null); },
      })}

      <BulkAddCredentialsModal
        open={showBulkModal}
        cameras={selectedCameras}
        loading={bulkAdding}
        error={error}
        onCancel={() => setShowBulkModal(false)}
        onConfirm={handleBulkAdd}
      />
    </>
  );
}
